import React from 'react';
import { motion } from 'framer-motion';
import { Github, Linkedin, Twitter, Mail, Heart, Instagram, Facebook} from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const footerLinks = {
    Services: [
      'Web Development',
      'Mobile Apps',
      'AI Solutions',
      'Cloud Services',
      'UI/UX Design'
    ],
    Company: [
      'About Us',
      'Portfolio',
      // 'Careers',
      // 'Blog',
      'Contact'
    ],
    // Resources: [
    //   'Case Studies',
    //   'Whitepapers',
    //   'Tech Stack',
    //   'API Documentation',
    //   'Support'
    // ]
  };

  const socialLinks = [
    { icon: Github, href: 'https://github.com/Developer-Hassan-mughal', label: 'Github' },
    { icon: Linkedin, href: 'https://www.linkedin.com/company/howztech/', label: 'LinkedIn' },
    // { icon: Twitter, href: 'https://twitter.com', label: 'Twitter' },
    { icon: Instagram, href: 'https://www.instagram.com/howztech/', label: 'Instagram' },
    { icon: Facebook, href: 'https://www.facebook.com/share/19QrHsRMte/', label: 'Facebook' },
    { icon: Mail, href: 'mailto:contact@howztech.com', label: 'Email' },
  ];

  return (
    <footer className="relative bg-black border-t border-white/25 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div 
          className="w-full h-full"
          style={{
            backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 1px, rgba(255,255,255,0.1) 1px, transparent calc(7.6923% + 1px)),
                             repeating-linear-gradient(-90deg, #fff, #fff 1px, transparent 1px, transparent 7.6923%)`
          }}
        />
      </div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-[7.6923%]">
        {/* Main Footer Content */}
        <div className="py-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12">
          {/* Brand Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-2"
          >
            <h3 className="text-3xl font-bold text-white mb-4">
              Howz<span className="text-[#00FFD1]">Tech</span>
            </h3>
            <p className="text-white/70 leading-relaxed mb-6 max-w-md">
              Empowering businesses with innovative digital solutions. We transform ideas into 
              cutting-edge technology that drives growth and success.
            </p>
            
            {/* Social Links */}
            <div className="flex gap-4">
              {socialLinks.map((social, index) => {
                const IconComponent = social.icon;
                return (
                  <motion.a
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    className="w-12 h-12 bg-white/10 hover:bg-[#00FFD1] text-white hover:text-black flex items-center justify-center transition-all duration-400 backdrop-blur-sm group"
                  >
                    <IconComponent size={20} strokeWidth={1.5} className="transition-transform duration-300 group-hover:scale-110" />
                  </motion.a>
                );
              })}
            </div>
          </motion.div>

          {/* Footer Links */}
          {Object.entries(footerLinks).map(([category, links], categoryIndex) => (
            <motion.div
              key={category}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: categoryIndex * 0.1 }}
            >
              <h4 className="text-white font-bold text-lg mb-4">{category}</h4>
              <ul className="space-y-3">
                {links.map((link, index) => (
                  <li key={index}>
                    <a
                      href="#"
                      className="text-white/70 hover:text-[#00FFD1] transition-colors duration-300 text-sm"
                    >
                      {link}
                    </a>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Newsletter Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="py-12 border-t border-white/25"
        >
          <div className="max-w-2xl mx-auto text-center">
            <h4 className="text-2xl font-bold text-white mb-3">
              Stay Updated
            </h4>
            <p className="text-white/70 mb-6">
              Subscribe to our newsletter for the latest tech insights and updates
            </p>
            <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 bg-black border border-white/25 text-white px-4 py-3 focus:outline-none focus:border-[#00FFD1] transition-colors duration-300"
              />
              <button className="bg-[#00FFD1] text-black px-8 py-3 font-medium transition-all duration-400 hover:bg-white whitespace-nowrap">
                Subscribe
              </button>
            </div>
          </div>
        </motion.div>

        {/* Bottom Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="py-8 border-t border-white/25"
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-white/60 text-sm">
            <p>
              © {currentYear} HowzTech. All rights reserved.
            </p>
            <div className="flex items-center gap-2">
              <span>Made with</span>
              <Heart className="text-[#00FFD1] fill-[#00FFD1]" size={16} />
              <span>by HowzTech Team</span>
            </div>
            <div className="flex gap-6">
              <a href="#" className="hover:text-[#00FFD1] transition-colors duration-300">
                Privacy Policy
              </a>
              <a href="#" className="hover:text-[#00FFD1] transition-colors duration-300">
                Terms of Service
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
